import React, { useContext, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box, useTheme } from '@mui/material';
import { AlertMessage } from '@eposnow/ui-core';
import validationSchema from '../helpers/validationSchema';
import StaffForm from './StaffForm';
import { Staff, StaffErrors } from '../StaffList';
import { UserContext } from '../../../context/UserContext';
import { STAFF_CACHE_KEY } from '../../../constants';
import useCreateUpdate from '../../../helperHooks/useCreateUpdate';
import { convertObjectToStringValues } from '../../../helpers/helpers';
import CreateEditCard from '../../../components/createEditComponents/CreateEditCard';
import { API_V1_HOSTNAME_KEY } from '../../../localStorageKeys';

export interface StaffCreateEdit {
  id: number;
  name: string;
  hourlyRate: string;
  staffRoleId: string;
  passcode: string;
  showAtAllLocations: boolean;
  mainLocationId: string;
  jobCodeIds: string; // array of numbers joined by comma (e.g. '3,4,7')
  ageVerificationGroupId: string;
}

const emptyObject: StaffCreateEdit = {
  id: null,
  name: '',
  staffRoleId: '',
  hourlyRate: null,
  passcode: '',
  showAtAllLocations: false,
  mainLocationId: '',
  jobCodeIds: '',
  ageVerificationGroupId: '',
};

type CreateEditStaffCardProps = {
  currentObject: Staff;
  resetObject: () => void;
  setSuccessMessage: (message: string) => void;
  errorMessage: string;
  setErrorMessage: (message: string) => void;
  errors: StaffErrors;
  setErrors: (obj: any) => void;
  mode: string;
};

const CreateEditStaffCard = ({
  currentObject,
  resetObject,
  setSuccessMessage,
  errorMessage,
  setErrorMessage,
  errors,
  setErrors,
  mode,
}: CreateEditStaffCardProps) => {
  const [initialObject, setInitialObject] = useState<StaffCreateEdit>(emptyObject);
  const [objectId, setObjectId] = useState<number>(0);
  const { t } = useTranslation();
  const theme = useTheme();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const { locale } = useContext(UserContext);

  const removeSavedObject = () => {
    const savedObjects = JSON.parse(sessionStorage.getItem(STAFF_CACHE_KEY) || '{}');
    delete savedObjects[`${objectId}`];
    sessionStorage.setItem(STAFF_CACHE_KEY, JSON.stringify(savedObjects));
  };

  const { isLoading, apiCall } = useCreateUpdate(
    mode,
    'staff',
    `${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/staff`,
    'staff list',
    STAFF_CACHE_KEY,
    resetObject,
    removeSavedObject,
    setSuccessMessage,
    setErrorMessage,
    setErrors,
    mode === 'create' ? '0' : currentObject.id.toString()
  );

  useEffect(() => {
    const savedObjects = JSON.parse(sessionStorage.getItem(STAFF_CACHE_KEY) || '{}');
    const newObjectId = mode === 'create' ? 0 : currentObject.id;
    setObjectId(newObjectId);
    const savedObject = savedObjects[`${newObjectId}`];

    let newInitialObject: StaffCreateEdit = { ...emptyObject, id: 0, ...savedObject };
    if (mode === 'create')
      sessionStorage.setItem(
        STAFF_CACHE_KEY,
        JSON.stringify({
          ...savedObjects,
          [`${newObjectId}`]: newInitialObject,
        })
      );
    else {
      const parsedCurrentObject = { ...currentObject };
      newInitialObject = {
        ...parsedCurrentObject,
        id: parsedCurrentObject.id,
        name: parsedCurrentObject.name || '',
        staffRoleId: parsedCurrentObject.staffRoleId?.toString() || '',
        passcode: parsedCurrentObject.passcode || '',
        hourlyRate: parsedCurrentObject.hourlyRate?.toString() || '',
        mainLocationId: parsedCurrentObject.mainLocationId?.toString() || '',
        jobCodeIds: parsedCurrentObject.jobCodeIds?.join(',') || '',
        ageVerificationGroupId: parsedCurrentObject.ageVerificationGroupId?.toString() || '',
      };
    }

    setInitialObject(newInitialObject);
  }, [currentObject]);

  const formik = useFormik({
    initialValues: initialObject,
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values, actions) => {
      if (isLoading) return;

      const requestValues: Staff = {
        id: values.id,
        name: values.name,
        staffRoleId: +values.staffRoleId || null,
        passcode: values.passcode,
        hourlyRate: values.hourlyRate ? +values.hourlyRate : null,
        showAtAllLocations: values.showAtAllLocations || false,
        mainLocationId: +values.mainLocationId || null,
        jobCodeIds: values.jobCodeIds.split(',').map(Number),
        ageVerificationGroupId: values.ageVerificationGroupId
          ? +values.ageVerificationGroupId
          : null,
      };

      actions.setSubmitting(false);
      if (mode === 'create') {
        apiCall(requestValues);
      } else {
        const oldObject: Staff = {
          id: initialObject.id,
          name: initialObject.name,
          staffRoleId: +initialObject.staffRoleId || null,
          passcode: initialObject.passcode,
          hourlyRate: initialObject.hourlyRate ? +initialObject.hourlyRate : null,
          showAtAllLocations: initialObject.showAtAllLocations || false,
          mainLocationId: +initialObject.mainLocationId || null,
          jobCodeIds: initialObject.jobCodeIds.split(',').map(Number),
          ageVerificationGroupId: initialObject.ageVerificationGroupId
            ? +initialObject.ageVerificationGroupId
            : null,
        };
        if (
          JSON.stringify(convertObjectToStringValues(requestValues)) !==
          JSON.stringify(convertObjectToStringValues(oldObject))
        ) {
          apiCall({ oldObject, newObject: requestValues });
        }
      }
    },
  });

  const clearError = (errorKey: string) => {
    setErrors(({ [errorKey as keyof StaffErrors]: _value, ...remaining }) => remaining);
  };

  useEffect(() => {
    if (formik.values?.id == null) return;

    const savedObject =
      mode === 'create'
        ? { ...emptyObject, id: 0 }
        : {
            ...currentObject,
            jobCodeIds: currentObject?.jobCodeIds?.join(',') || '',
            ageVerificationGroupId: currentObject.ageVerificationGroupId?.toString() || '',
          };

    setHasUnsavedChanges(
      JSON.stringify(convertObjectToStringValues(formik.values)) !==
        JSON.stringify(convertObjectToStringValues(savedObject))
    );
  }, [JSON.stringify(formik.values)]);

  return (
    <CreateEditCard
      resetObject={resetObject}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      mode={mode}
      translationKey="staff"
    >
      <Box padding={2}>
        <StaffForm
          formik={formik}
          cancelAction={() => {
            resetObject();
            removeSavedObject();
          }}
          objectId={objectId.toString()}
          isSubmitting={isLoading}
          errors={errors}
          clearError={clearError}
        />
        {hasUnsavedChanges && (
          <AlertMessage type="warning" locale={locale} theme={theme}>
            {t('errors.unsavedChanges')}
          </AlertMessage>
        )}
      </Box>
    </CreateEditCard>
  );
};

export default CreateEditStaffCard;
